import memoize from 'lodash/memoize';
import labels from './const';
import stableStringify from './stableStringify';

// memoize to keep referential identity of the same filters created multiple times
const createFilter = memoize(
  (type, label, data, id) => ({
    type,
    label,
    data,
    id: id || encodeURIComponent(stableStringify([type, label, data])), // deterministic id based on inputs to have the same id's after refreshing
  }),
  (...args) => stableStringify(args),
);

const hasFilter = (filters = [], type) => !!filters.find(f => f.type === type);

const IN_DISPUTE = 'inDispute';
const CHASING_CUSTOMERS = 'chasingCustomers';
const NOT_CHASING_CUSTOMERS = 'notChasingCustomers';
const CHASING_INVOICES = 'chasingInvoices';
const NOT_CHASING_INVOICES = 'notChasingInvoices';
const PAID_INVOICES = 'paidInvoices';
const PENDING_INVOICES = 'pendingInvoices';
const COLLECTING_INVOICES = 'collectingInvoices';
const WAITING_CONTRACT = 'waitingContract';
const COLLECTED_INVOICES = 'collectedInvoices';
const MISSING_NAME = 'missingName';
const MISSING_EMAIL = 'missingEmail';
const MISSING_ADDRESS = 'missingAddress';
const MISSING_MOBILE = 'missingMobile';
const AGED_DEBT = 'agedDebt';
const READY_FOR_COLLECTION = 'readyForCollection';
const SCHEDULE = 'schedule';
const CATEGORY = 'category';
const SCORE = 'score';
const CHASEFEED = 'chasefeed';
const INVOICEIDS = 'invoiceIds';
const PAYMENT_PLAN = 'onPaymentPlan';
const NOT_ON_PAYMENT_PLAN = 'noPaymentPlan';
const LATE_PAYMENT_FEES = 'latePaymentFees';
const NO_LATE_PAYMENT_FEES = 'noLatePaymentFees';
const EARLY_PAYMENT_DISCOUNTS = 'earlyPaymentDiscounts';
const NO_EARLY_PAYMENT_DISCOUNTS = 'noEarlyPaymentDiscounts';
const NET_BALANCE_ABOVE_ZERO = 'netBalanceAboveZero';
const NET_BALANCE_IS_ZERO = 'netBalanceIsZero';
const NET_BALANCE_BELOW_ZERO = 'netBalanceBelowZero';

const filters = {
  inDispute: createFilter(IN_DISPUTE, labels.inDispute),
  chasingCustomers: createFilter(CHASING_CUSTOMERS, labels.ChasingCustomersLabel),
  notChasingCustomers: createFilter(NOT_CHASING_CUSTOMERS, labels.NotChasingCustomersLabel),
  chasingInvoices: createFilter(CHASING_INVOICES, labels.ChasingInvoicesLabel),
  notChasingInvoices: createFilter(NOT_CHASING_INVOICES, labels.NotChasingInvoicesLabel),
  paidInvoices: createFilter(PAID_INVOICES, labels.PaidInvoicesLabel),
  pendingInvoices: createFilter(PENDING_INVOICES, labels.PendingCollectionLabel),
  collectingInvoices: createFilter(COLLECTING_INVOICES, labels.CollectingLabel),
  waitingContract: createFilter(WAITING_CONTRACT, labels.WaitingContractLabel),
  collectedInvoices: createFilter(COLLECTED_INVOICES, labels.CollectedLabel),
  missingName: createFilter(MISSING_NAME, labels.MissingNameLabel),
  missingEmail: createFilter(MISSING_EMAIL, labels.MissingEmailLabel),
  missingAddress: createFilter(MISSING_ADDRESS, labels.MissingAddressLabel),
  missingMobile: createFilter(MISSING_MOBILE, labels.MissingMobileLabel),
  readyForCollection: createFilter(READY_FOR_COLLECTION, labels.ReadyForCollectionLabel),
  overdue1to30: createFilter(AGED_DEBT, labels.Overdue0to30Label, { from: 0, to: 30 }),
  overdue31to60: createFilter(AGED_DEBT, labels.Overdue31to60Label, { from: 31, to: 60 }),
  overdue61to90: createFilter(AGED_DEBT, labels.Overdue61to90Label, { from: 61, to: 90 }),
  overdue90plus: createFilter(AGED_DEBT, labels.Overdue90plusLabel, { from: 91, to: undefined }),
  onPaymentPlan: createFilter(PAYMENT_PLAN, labels.onPaymentPlanLabel),
  noPaymentPlan: createFilter(NOT_ON_PAYMENT_PLAN, labels.noPaymentPlanLabel),
  latePaymentFees: createFilter(LATE_PAYMENT_FEES, labels.latePaymentFeesLabel),
  noLatePaymentFees: createFilter(NO_LATE_PAYMENT_FEES, labels.noLatePaymentFeesLabel),
  earlyPaymentDiscounts: createFilter(EARLY_PAYMENT_DISCOUNTS, labels.earlyPaymentDiscountsLabel),
  noEarlyPaymentDiscounts: createFilter(
    NO_EARLY_PAYMENT_DISCOUNTS,
    labels.noEarlyPaymentDiscountsLabel,
  ),
  netBalanceAboveZero: createFilter(NET_BALANCE_ABOVE_ZERO, labels.netBalanceAboveZeroLabel),
  netBalanceIsZero: createFilter(NET_BALANCE_IS_ZERO, labels.netBalanceIsZeroLabel),
  netBalanceBelowZero: createFilter(NET_BALANCE_BELOW_ZERO, labels.netBalanceBelowZeroLabel),
};

export {
  filters,
  createFilter,
  CHASING_CUSTOMERS,
  NOT_CHASING_CUSTOMERS,
  CHASING_INVOICES,
  NOT_CHASING_INVOICES,
  PAID_INVOICES,
  PENDING_INVOICES,
  WAITING_CONTRACT,
  COLLECTING_INVOICES,
  COLLECTED_INVOICES,
  MISSING_NAME,
  MISSING_EMAIL,
  MISSING_ADDRESS,
  MISSING_MOBILE,
  AGED_DEBT,
  READY_FOR_COLLECTION,
  SCHEDULE,
  INVOICEIDS,
  CHASEFEED,
  IN_DISPUTE,
  PAYMENT_PLAN,
  CATEGORY,
  NOT_ON_PAYMENT_PLAN,
  EARLY_PAYMENT_DISCOUNTS,
  NO_EARLY_PAYMENT_DISCOUNTS,
  LATE_PAYMENT_FEES,
  NO_LATE_PAYMENT_FEES,
  hasFilter,
  SCORE,
  NET_BALANCE_ABOVE_ZERO,
  NET_BALANCE_IS_ZERO,
  NET_BALANCE_BELOW_ZERO,
};
